import { Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Input } from '@/components/ui/input';

interface FileUploadProps {
    selectedFiles: File[];
    onFilesChange: (files: File[]) => void;
    accept?: string;
    multiple?: boolean;
    title?: string;
    height?: number;
}

export function FileUpload({
    selectedFiles,
    onFilesChange,
    accept = '.pdf,.doc,.docx',
    multiple = true,
    title = 'Select to upload or drag files here',
    height,
}: FileUploadProps) {
    const supportedFileTypes = accept
        .split(',')
        .map((type) => type.replace('.', '').toUpperCase())
        .join(', ');

    const validateFiles = (files: File[]) => {
        const validTypes = accept.split(',').map((type) => type.trim());
        const validFiles = files.filter((file) => {
            const fileExtension =
                '.' + file.name.split('.').pop()?.toLowerCase();
            return validTypes.includes(fileExtension);
        });

        if (validFiles.length !== files.length) {
            const rejectedCount = files.length - validFiles.length;
            toast.error(
                `${rejectedCount} file${rejectedCount > 1 ? 's' : ''} rejected. Only ${supportedFileTypes} files are allowed.`,
            );
        }

        return validFiles;
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const files = Array.from(e.dataTransfer.files);
        const validFiles = validateFiles(files);

        if (validFiles.length > 0) {
            if (!multiple) {
                // Single file mode: replace with first valid file
                onFilesChange([validFiles[0]]);
            } else {
                onFilesChange([...selectedFiles, ...validFiles]);
            }
        }
    };

    return (
        <div data-testid="FileUpload">
            <div
                className="relative border-2 border-dashed rounded-lg p-8 text-center hover:border-blue-500 hover:bg-blue-50 transition-colors duration-200 cursor-pointer flex flex-col items-center justify-center"
                style={height ? { height } : undefined}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                data-testid="FileUpload__dropZone"
            >
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                <div className="space-y-2">
                    <p
                        className="text-lg font-medium text-gray-500"
                        data-testid="FileUpload__title"
                    >
                        {title}
                    </p>
                    <span
                        className="text-sm text-gray-400"
                        data-testid="FileUpload__description"
                    >
                        Supported file types: {supportedFileTypes}
                    </span>
                </div>
                <Input
                    type="file"
                    accept={accept}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    data-testid="FileUpload__input"
                    multiple={multiple}
                    onChange={(e) => {
                        if (e.target.files) {
                            const files = Array.from(e.target.files);
                            const validFiles = validateFiles(files);
                            if (validFiles.length > 0) {
                                if (!multiple) {
                                    // Single file mode: replace with first valid file
                                    onFilesChange([validFiles[0]]);
                                } else {
                                    onFilesChange([
                                        ...selectedFiles,
                                        ...validFiles,
                                    ]);
                                }
                            }
                        }
                    }}
                />
            </div>
        </div>
    );
}
